import { RiArrowDownSLine, RiCheckLine, RiLoader4Line, RiPrinterLine } from '@remixicon/react';
import html2canvas from 'html2canvas';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import DeleteButton from 'src/components/atoms/buttons/DeleteButton';
import { AgButton, AgDropdownItem, AgDropdownMenu, AgTooltip, PolicyLockHint } from 'src/components/ui';
import { notify } from 'src/lib/notify';
import { useDeletePalletTags, usePrintPalletTags } from 'src/modules/palletTags/api/palletTags.api';
import { PalletTagsPreview } from 'src/modules/palletTags/components/PalletTagsPreview';
import {
  ConfiguredPrinter,
  PrintManagerStatus,
  usePrintManager,
} from 'src/modules/palletTags/providers/PrintManagerProvider';
import {
  type Lot,
  PalletTagLayout,
  type PalletTag as PalletTagRow,
  type ProductionRunForPalletTags,
} from 'src/modules/palletTags/types';
import usePrinterPreferences from '../hooks/usePrinterPreferences';
import { usePalletTagsDeleteDecision } from '../policy/usePalletTagsDeleteDecision';
import { basicTemplate } from './PalletTag';

import rgbaToZ64 from './z64';

const defaultWidth = basicTemplate.width;
const defaultHeight = basicTemplate.height;

type Props = {
  refetch?: () => void;
  productionRun?: ProductionRunForPalletTags;
  lot?: Lot;
  lots?: Lot[];
  palletTags: PalletTagRow[];
  setPalletTags: (tags: PalletTagRow[]) => void;
  selectedTags: PalletTagRow[];
  clearSelection: () => void;
  selectUnprinted: () => void;
};

export default function PalletTagsToolbar({
  lot,
  lots,
  productionRun,
  refetch,
  palletTags,
  setPalletTags,
  selectedTags,
  clearSelection,
  selectUnprinted,
}: Props) {
  const componentRef = useRef<HTMLDivElement>(null);
  const selectedTagIds = useMemo(() => selectedTags.map((tag) => tag.id), [selectedTags]);

  const { print: printPalletTags } = usePrintPalletTags(palletTags, setPalletTags, selectedTagIds);
  const { remove: deletePalletTags } = useDeletePalletTags(refetch);
  // A used tag refuses the whole batch on the server, so the toolbar refuses it first, same sentence.
  const deleteDecision = usePalletTagsDeleteDecision(selectedTags);

  const [busy, setBusy] = useState(false);

  const printPageStyle = useCallback(() => {
    const refTag = selectedTags[0]?.tagLayout?.schema;
    const width = refTag && typeof refTag === 'object' && 'width' in refTag ? refTag.width : undefined;
    const height = refTag && typeof refTag === 'object' && 'height' in refTag ? refTag.height : undefined;
    return `width: '${width ?? defaultWidth}';height: '${height ?? defaultHeight}';`;
  }, [selectedTags]);

  const sameSizes = useMemo(() => {
    const refSchema = selectedTags[0]?.tagLayout?.schema as PalletTagLayout;
    const refWidth = refSchema?.width ?? defaultWidth;
    const refHeight = refSchema?.height ?? defaultHeight;

    return selectedTags.every((tag) => {
      const schema = tag.tagLayout?.schema as PalletTagLayout;
      const width = schema?.width ?? defaultWidth;
      const height = schema?.height ?? defaultHeight;

      return width === refWidth && height === refHeight;
    });
  }, [selectedTags]);

  const printManager = usePrintManager();
  const printPreferences = usePrinterPreferences();

  // Rasterize the selected tags into a single ZPL payload for ClientPrintJob.binaryPrinterCommands. Separate from handleManagedPrint so the provider's tryPrint() owns the JSPM dispatch lifecycle (AbortController, timeout, fallback) without needing componentRef.
  const buildZplForJspm = useCallback(async (printer: ConfiguredPrinter, tags: PalletTagRow[]): Promise<string> => {
    const style = document.createElement('style');
    document.head.appendChild(style);
    style.sheet?.insertRule('body > div:last-child img { display: inline-block; }');

    const canvasDPI = 96;
    const defaultDPI = 203;
    const scale = (printer.paperSize?.dpi ?? defaultDPI) / canvasDPI;

    try {
      const tagRenderings = await Promise.all(
        tags.map(async (tag, i) => {
          const node = componentRef.current?.children[i] as HTMLElement;
          const canvas = await html2canvas(node, { removeContainer: true, scale });
          const imageData = canvas.getContext('2d')!.getImageData(0, 0, canvas.width, canvas.height);
          const res = rgbaToZ64(imageData.data, canvas.width, { rotate: printer.rotation });

          const offset = `^FO${printer.offset?.x ?? 0},${printer.offset?.y ?? 0}`;
          const count = tag.tagLayout?.tagType === 'CASE' ? tag.cartonQuantity : 1;

          return `^XA
              ${offset}
              ^GFA,${res.length},${res.length},${res.rowlen},${res.z64}
              ^PQ${count}
              ^XZ`;
        })
      );

      const batchZPL = printer.batchZPL ? `^XA\n${printer.batchZPL}\n^XZ\n` : '';
      return `${batchZPL}${tagRenderings.join('\n')}`;
    } finally {
      style.remove();
    }
  }, []);

  const handleBrowserPrint = useReactToPrint({
    content: () => componentRef.current,
    pageStyle: printPageStyle,
    onAfterPrint: () => printPalletTags({ ids: selectedTagIds }),
  });

  // provides the printers matching the dimensions of the selected tags
  const matchingPrinters = useMemo(() => {
    if (printManager.status === PrintManagerStatus.DISABLED) {
      return [];
    }

    const tag = selectedTags[0]?.tagLayout?.schema ?? basicTemplate;
    const refSchema = tag as PalletTagLayout;

    if (!refSchema) {
      return [];
    }

    const filteredPrinters = printManager.printers.filter((printer) => {
      const paperSize = printer.paperSize;
      const width = paperSize.width / paperSize.dpi;
      const height = paperSize.height / paperSize.dpi;

      const schemaWidth = parseFloat(refSchema.width);
      const schemaHeight = parseFloat(refSchema.height);

      return Math.abs(width - schemaWidth) <= 0.25 && Math.abs(height - schemaHeight) <= 0.25;
    });

    return filteredPrinters?.length ? filteredPrinters : printManager.printers;
  }, [selectedTags, printManager]);

  const showPrinterDropdown = matchingPrinters.length > 0;

  const printErrorReason = useMemo(() => {
    if (!sameSizes) return 'Tags must be the same size to print together';
    return undefined;
  }, [sameSizes]);

  const tagCountToBePrinted = useMemo(() => {
    return selectedTags.reduce((acc, tag) => {
      const count = tag.tagLayout?.tagType === 'CASE' ? tag.cartonQuantity : 1;

      return acc + count;
    }, 0);
  }, [selectedTags]);

  const handlePrintClick = useCallback(async () => {
    if (!sameSizes || busy) return;
    setBusy(true);

    try {
      const preferredPrinter = matchingPrinters.find(
        (printer) =>
          printer.name === printPreferences.preferences[`${printer.paperSize.width}x${printer.paperSize.height}`]
      );
      const printer = preferredPrinter ?? matchingPrinters[0];

      // No printer available → straight to browser dialog. tryPrint also
      // takes this path for DISABLED status, but resolving locally lets
      // us avoid even building ZPL for no-printer cases.
      if (!printer) {
        handleBrowserPrint();
        return;
      }

      const zpl = await buildZplForJspm(printer, selectedTags);
      const result = await printManager.tryPrint({ printer, zpl, fallback: handleBrowserPrint });

      if (result.kind === 'jspm') {
        printPalletTags({ ids: selectedTagIds });
      } else {
        // Surface the fallback so the user knows why the OS print dialog appeared instead of silent printing.
        notify.info('Printed via browser dialog (silent printer unreachable)');
      }
    } catch (err) {
      console.error('print click failed', err);
      notify.error('Error preparing tags for print');
    } finally {
      setBusy(false);
    }
  }, [
    sameSizes,
    busy,
    matchingPrinters,
    printPreferences.preferences,
    buildZplForJspm,
    selectedTags,
    printManager,
    handleBrowserPrint,
    printPalletTags,
    selectedTagIds,
  ]);

  return (
    <>
      <div className="flex items-center gap-2 mb-2">
        <AgButton variant="ghost" onPress={selectUnprinted}>
          Select Unprinted
        </AgButton>
        <div className="grow" />
        <PolicyLockHint decision={deleteDecision}>
          <DeleteButton
            disabled={selectedTags.length === 0 || !deleteDecision.allowed}
            handleConfirm={() => {
              deletePalletTags({ ids: selectedTags.map((tag) => tag.id) }).then(() => {
                setPalletTags(palletTags.filter((tag) => !selectedTagIds.includes(tag.id)));
                clearSelection();
              });
            }}
            title="Delete Tags"
            label={`Delete ${selectedTags.length || ''} Tag${selectedTags.length === 1 ? '' : 's'}`}
            variant="button"
            size="small"
            prompt="Are you sure you would like to delete the selected pallet tags?"
          />
        </PolicyLockHint>
        <AgTooltip content={printErrorReason}>
          <span className="inline-flex">
            <AgButton
              isDisabled={printErrorReason !== undefined || busy}
              variant="white"
              className={showPrinterDropdown ? 'rounded-r-none' : ''}
              onPress={handlePrintClick}
              data-testid="print-tags-button"
            >
              {busy ? <RiLoader4Line size={18} className="animate-spin" /> : <RiPrinterLine size={18} />}{' '}
              {tagCountToBePrinted
                ? `Print ${tagCountToBePrinted} Tag${tagCountToBePrinted === 1 ? '' : 's'}`
                : 'Print All Tags'}
            </AgButton>
            {showPrinterDropdown && (
              <PrinterOptions
                matchingPrinters={matchingPrinters}
                disabled={printErrorReason !== undefined || busy}
                printPreferences={printPreferences}
              />
            )}
          </span>
        </AgTooltip>
      </div>
      <div style={{ position: 'absolute', top: '-10000px', left: '-10000px' }}>
        <PalletTagsPreview
          palletTags={selectedTags}
          ref={componentRef}
          lot={lot}
          lots={lots}
          productionRun={productionRun}
        />
      </div>
    </>
  );
}

const PrinterOptions = ({
  matchingPrinters,
  disabled,
  printPreferences,
}: {
  matchingPrinters: ConfiguredPrinter[];
  disabled: boolean;
  printPreferences: ReturnType<typeof usePrinterPreferences.getState>;
}) => {
  return (
    <AgDropdownMenu
      variant="white"
      className="rounded-l-none border-l-0"
      showChevron={false}
      icon={<RiArrowDownSLine size={18} />}
      isDisabled={disabled}
      items={matchingPrinters.map((printer) => {
        const isPrimary =
          printer.name === printPreferences.preferences[`${printer.paperSize.width}x${printer.paperSize.height}`];

        return (
          <AgDropdownItem
            key={printer.name}
            icon={isPrimary ? <RiCheckLine size={18} /> : <span className="w-4.5" />}
            onAction={() => {
              printPreferences.updatePreference(`${printer.paperSize.width}x${printer.paperSize.height}`, printer.name);
            }}
          >
            {printer.name}
          </AgDropdownItem>
        );
      })}
    />
  );
};
